import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getData, addData, updateData, deleteData } from '../../services/registrationContentServices';
import { Modal, Button, Form } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DataTable from 'react-data-table-component';
import Header from '../../components/Layout/Header';
import Footer from '../../components/Layout/Footer';
import Sidebar from '../../components/Layout/Sidebar';
import Spinner from '../../components/Spinner';
import { toast } from 'react-toastify';
import { isQuillContentEmpty } from '../../utils/common';

const Registrationcontent = () => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [dataList, setDataList] = useState([]);
  const [form, setForm] = useState({ id: '', rc_title: '', rc_description: ''});
  const [modalType, setModalType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  
  const modules = {
  toolbar: [
    // Font family and size
    [{ 'font': [] }, { 'size': ['small', false, 'large', 'huge'] }],

    // Headings
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

    // Text styles
    ['bold', 'italic', 'underline', 'strike'],

    // Subscript / Superscript
    [{ 'script': 'sub' }, { 'script': 'super' }],

    // Blockquote and code block
    ['blockquote', 'code-block'],

    // Color and background
    [{ 'color': [] }, { 'background': [] }],

    // Text alignment and direction (RTL)
    [{ 'align': [] }, { 'direction': 'rtl' }],

    // Ordered / bullet list
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],

    // Indent and outdent
    [{ 'indent': '-1' }, { 'indent': '+1' }],

      ['link',],

    // Clear formatting
    ['clean']
  ]
};
  
  const formats = [
  'font', 'size',
  'header',
  'bold', 'italic', 'underline', 'strike',
  'script',
  'blockquote', 'code-block',
  'color', 'background',
  'align', 'direction',
  'list', 'bullet', 'indent',
  'link', 'image', 'video'
];

  // Fetch data list
  const fetchData = useCallback(async () => {
    setLoading(true);
    try{
    const data = await getData();
    setDataList(data);
    }
    catch (error) {
      console.error('Error fetching data:', error);
    } finally {
       setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const validateForm = () => {
    const newErrors = {};
    if (!form.rc_title) newErrors.rc_title = "Title is required";
    if (isQuillContentEmpty(form.rc_description)) newErrors.rc_description = "Description is required";
    
    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys({ ...errors, ...newErrors }).every((key) => !errors[key] && !newErrors[key]);
  };

  const handleChange = (e) => {
    try {
    const { name, value } = e.target;
    let updatedData = { ...form };
    let updatedErrors = { ...errors };
    updatedData[name] = value;
        if (value) {
        updatedErrors[name] = ''; // Clear error if corrected
      }
      setForm(updatedData);
      setErrors(updatedErrors);
    }
    catch (error) {
      console.error('Error in handleChange:', error);
    }
  };

  // Handle change for Quill editor
  const handleDescriptionChange = (value) => {
    setErrors((prev) => ({ ...prev, rc_description: isQuillContentEmpty(value) ? 'Description is required' : '' }));
    setForm((prev) => ({ ...prev, rc_description: value}));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
        return
      }
    setLoading(true);
    try{
    let response;
    if (modalType === 'add') {
      response = await addData(form);
      if (response.status === 200||201) {
        fetchData(); // Refresh data after update
        toast.success("Saved successfully!", {
                                position: 'top-right',
                              });
      } else {
        toast.error(response?.message ||"Failed to update data. Please try again.", {
                                position: 'top-right',
                              });
      }
    } else if (modalType === 'edit') {
      response =await updateData(form);
      if (response.status === 200) {
        fetchData(); // Refresh data after update
        toast.success("Updated successfully!", {
                                position: 'top-right',
                              });
      } else {
        toast.error(response?.message ||"Failed to update data. Please try again.", {position: 'top-right'});
      }

    }
    fetchData();  // Re-fetch data after adding/editing
    setShowModal(false);
  }
  catch (error) {
      console.error('Error updating data:', error);
    } 
  finally {
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  };

  const handleDelete = async () => {
    try{
    if (selectedData) {
      // Optimistically remove the data from the list
      setDataList((prev) => prev.filter((data) => data.id !== selectedData.id));
      // Proceed with the actual delete request
      await deleteData(selectedData);
      setShowDeleteConfirmModal(false);
      toast.success("Deleted successfully!", {
                                position: 'top-right',
                              });
    }
  }
  catch(error){
    console.log("error during delete  data ",error);
  }
  };

  const openModal = (type, data = { id: '', rc_title: '', rc_description: '' }) => {
    setModalType(type);
    setForm(data);
    setShowModal(true);
  };
  
  const openDeleteConfirmModal = (data) => {
    setSelectedData(data);
    setShowDeleteConfirmModal(true);
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Memoized filtered data based on search query
  const filteredData = useMemo(() => {
    const lowerSearch = searchText.toLowerCase();
    const  filtered =  dataList.filter(
      (data) =>
      data.rc_title.toLowerCase().includes(lowerSearch)
    );
    const sortedData = [...filtered].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    return sortedData;
  }, [dataList, searchText]);

  const columns = [
    {
      name: '#',
      selector: (row, index) => (currentPage - 1) * 10 + (index + 1),
      sortable: false,
      width: '80px',
    },
    {
      name: 'Title',
      selector: (row) => row.rc_title,
      cell: (row) => (
            <span title={row.rc_title}>
              {row.rc_title}
            </span>
          ),
      sortable: true,
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div>
          <Button className="me-2" variant="warning" size="sm" onClick={() => openModal('edit', row)} > Edit </Button>
          <Button variant="danger" size="sm" onClick={() => openDeleteConfirmModal(row)} > Delete </Button>
        </div>
      ),
    },
  ];

  useEffect(() => {
        const tooltipMap = {
          'ql-bold': 'Bold',
          'ql-italic': 'Italic',
          'ql-underline': 'Underline',
          'ql-strike': 'Strikethrough',
          'ql-script[value="sub"]': 'Subscript',
          'ql-script[value="super"]': 'Superscript',
          'ql-blockquote': 'Blockquote',
          'ql-code-block': 'Code Block',
          'ql-color': 'Text Color',
          'ql-background': 'Background Color',
          'ql-align': 'Align',
          'ql-direction': 'Text Direction',
          'ql-list[value="ordered"]': 'Ordered List',
          'ql-list[value="bullet"]': 'Bullet List',
          'ql-indent[value="-1"]': 'Outdent',
          'ql-indent[value="+1"]': 'Indent',
          'ql-link': 'Insert Link',
          'ql-clean': 'Clear Formatting',
        };
  
      const setTooltips = () => {
        Object.entries(tooltipMap).forEach(([selector, title]) => {
          const el = document.querySelector(`.ql-toolbar .ql-formats .${selector}`);
          if (el && !el.title) {
            el.setAttribute('title', title);
          }
        });
      };
  
      const observer = new MutationObserver((mutations, obs) => {
        const toolbarReady = document.querySelector('.ql-toolbar');
        if (toolbarReady) {
          setTooltips();
          obs.disconnect(); // Stop observing once done
        }
      });
  
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
  
    return () => observer.disconnect() // Clean up on unmount
    }, []);
    
  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />
        {loading ? (
          <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "300px" }}>
          <Spinner />
        </div>
      ) :(
        <div>
        <div className="container-fluid p-4">
          <div className="row">
            <div className="col-8">
              <p className='mb-3 fs-4'>Basic Setup & File upload / Registration Setup <b>/ Registration QA</b></p>
              {/* <h2 className='mb-3'>Registration Setup / Registration QA</h2> */}
            </div>
            <div className="col-4 text-end">
              <button className="btn btn-primary mb-3 form-label-custom" onClick={() => openModal('add')}>
                Add
              </button>
            </div>
          </div>

          {/* Data Table for data */}
          <DataTable
            columns={columns}
            data={filteredData}
            pagination
            search
            highlightOnHover
            responsive
            striped
            paginationPerPage={10}
            paginationComponentOptions={{
            noRowsPerPage: true,
            }}
            onChangePage={handlePageChange}
            subHeader
            subHeaderComponent={
              <div>
                <input className="form-control" type="text" placeholder="Search" value={searchText}
                  onChange={handleSearchChange}
                />
              </div>
            }
          />

          {/* Modal for Add/Edit data */}
          <Modal className="modal-lg" show={showModal} onHide={() => setShowModal(false)} backdrop="static">
            <Modal.Header closeButton>
              <Modal.Title className='fs-3'>{modalType === 'add' ? 'Add' : 'Edit'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>
              <div className="row">               
                <Form.Group className="mb-3">
                  <Form.Label className='form-label-custom'>Title <small className='text-danger'> * </small></Form.Label>
                  <Form.Control type="text" name="rc_title" value={form.rc_title} onChange={handleChange}  isInvalid={!!errors.rc_title}/>
                  <Form.Control.Feedback type="invalid">{errors.rc_title}</Form.Control.Feedback>
                </Form.Group>
               <Form.Group className="mb-3">
                    <Form.Label className='form-label-custom'>Description <small className='text-danger'> * </small></Form.Label>
                    <ReactQuill
                        value={form.rc_description}
                        onChange={handleDescriptionChange}
                        theme="snow"
                        modules={modules}
                        formats={formats}
                        placeholder="Enter description"
                        className={`react-quill-editor ${errors.rc_description ? 'border border border-1 border-danger rounded' : ''}`} 
                    />
                    {errors.rc_description && (
                    <div className="invalid-feedback d-block">
                      {errors.rc_description}
                    </div>
                  )}
                </Form.Group>
                
                </div>
             
            <Modal.Footer className='border-0'>
              <Button variant="secondary" onClick={() => setShowModal(false)}> Cancel </Button>
              <Button variant="primary" onClick={handleSubmit}> {modalType === 'add' ? 'Add' : 'Save'} </Button>
            </Modal.Footer>
             </Form>
            </Modal.Body>
          </Modal>

          {/* Delete Confirmation Modal */}
          <Modal show={showDeleteConfirmModal} onHide={() => setShowDeleteConfirmModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>Delete</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>Are you sure you want to delete this row?</p>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowDeleteConfirmModal(false)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDelete}>
                Yes, Delete
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
        <Footer />
         </div>
        )}
      </div>
    </div>
  );
};

export default Registrationcontent;